import React, { useState, useMemo } from 'react'
import { BaseDrawer } from '@/components/drawer/base-drawer'
import { layerManage, showLayer } from '@/utils/layer'
import FormTitle from '../../../components/form/form-title/form-title'
import Iconfont from '@/components/iconfont/iconfont'
import RemarkPopover from '../../../components/remark-popover/remark-popover'
import { Row, Column, Expaned } from '@/components/layout/layout'
import { getBEMCls } from '@/utils/classname'
import Table, { ColumnsType } from 'antd/lib/table'
import Big from 'big.js'
import LayoutInput from '../../../components/form/layout-input/layout-input'
import Button from '@/components/button/button'
import { deepClone, setObj, uniqBy } from '@/utils'
import SelectGoods, { useSelectGoods } from '../../../components/select-goods/select-goods'
import { useRequest, useMount } from 'ahooks'
import { $post } from '@/api'
import $msg from '@/utils/message'
import StockUpdateDetail from '../stock-update-detail/stock-update-detail'
import { IUpdateDetail } from '../stock-update-detail/update-detail-info'
import useSetDataTogether from '@/utils/hooks/useSetDataTogether'
import { Spin } from 'antd'
import { Goods } from '@/model/types/goods'

interface GoodsList {
    goods: Goods
    stock_actual: number | string
    remark: string
}

interface StockUpdateFormProps {
    refresh: () => void
    inventoryId?: number
}

const StockUpdateForm: React.FC<StockUpdateFormProps> = props => {
    const cls = (...arg: string[]) => getBEMCls('stock-update', ...arg)

    const [invertList, setInvertList] = useState<GoodsList[]>([])
    const [orderInfo, setOrderInfo] = useState<IUpdateDetail | null>(null)
    const [loading, setLoading] = useState<boolean>(!!props.inventoryId)

    const setAllData = useSetDataTogether()
    const { reduceGoods: reduceGood, initGoods } = useSelectGoods()

    const btnDisabled = useMemo(
        () => !invertList.length || invertList.some(list => !list.stock_actual.toString().trim().length),
        [invertList],
    )

    const getDetailReq = useRequest(
        () =>
            $post({
                url: '/warehouse/getinventorydetail',
                params: {
                    inventory_id: props.inventoryId,
                },
            }),
        {
            manual: true,
            onSuccess: (res: IUpdateDetail) => {
                setAllData(() => {
                    // 批量更新
                    setOrderInfo(res)
                    setInvertList(
                        res.inventory_goods.map(g => ({
                            goods: g.goods,
                            remark: g.remark,
                            stock_actual: g.stock_actual,
                        })),
                    )
                    setLoading(false)
                })
                initGoods(res.inventory_goods.map(g => g.goods))
            },
        },
    )

    useMount(() => {
        props.inventoryId && getDetailReq.run()
    })

    const columns: ColumnsType<GoodsList> = [
        {
            title: '#',
            key: 'index',
            width: 40,
            align: 'center',
            render: (value, record, index) => index + 1,
        },
        {
            title: '商品名',
            key: 'name',
            ellipsis: true,
            dataIndex: 'goods',
            render: val => val.name,
        },
        {
            title: '上架',
            width: 70,
            dataIndex: 'goods',
            render: val => (
                <Row>
                    {val.status === 0 || val.on_mall ? (
                        <React.Fragment>
                            <span className={cls('icon')}>
                                {val.on_mall && <Iconfont name="icon2shangjiaweishangcheng" size={20} />}
                            </span>
                            <span>
                                {val.status === 0 && (
                                    <Iconfont
                                        name="icon2shangjiashouyintai"
                                        size={20}
                                        style={{
                                            marginLeft: '8px',
                                        }}
                                    />
                                )}
                            </span>
                        </React.Fragment>
                    ) : (
                        <span>
                            <Iconfont name="iconweishangjia1" size={35}></Iconfont>
                        </span>
                    )}
                </Row>
            ),
        },
        {
            title: '系统库存',
            key: 'stock_original',
            align: 'right',
            dataIndex: 'goods',
            width: 80,
            render: val => val.stock,
        },
        {
            title: '实际库存',
            key: 'stock_actual',
            dataIndex: 'stock_actual',
            render: (val, record, index) => (
                <Row data-align-items-center>
                    <LayoutInput
                        value={val}
                        onBlur={e => {
                            setInvertList(deepClone(setObj(invertList, `[${index}].stock_actual`, e)))
                        }}
                    />
                </Row>
            ),
        },
        {
            title: '库存差异',
            key: 'stock_surplus',
            width: 80,
            align: 'right',
            render: (val, record) => {
                const changeAmount = +Big(parseFloat(record.stock_actual + '') || 0).minus(record.goods.stock)

                return <div className={`${changeAmount < 0 ? 'danger-color' : ''}`}>{changeAmount}</div>
            },
        },
        {
            title: '备注',
            key: 'remark',
            dataIndex: 'remark',
            align: 'right',
            render: (val, record, index) => (
                <RemarkPopover
                    value={val}
                    onConfirm={e => {
                        setInvertList(deepClone(setObj(invertList, `[${index}].remark`, e)))
                    }}
                >
                    {val ? (
                        <div className={cls('remark')}>{val}</div>
                    ) : (
                        <Row data-justify-content-flex-end data-cursor-pointer data-align-items-center>
                            <div data-font-size-14 data-color-999>
                                请设置
                            </div>
                            <Iconfont data-margin-left-4 name="icon6youjiantou-20" size={16} />
                        </Row>
                    )}
                </RemarkPopover>
            ),
        },
        {
            title: '',
            key: 'reduce',
            width: 40,
            align: 'center',
            render: (val, rec, index) => (
                <div data-cursor-pointer>
                    <Iconfont
                        name="icon6yichu-20"
                        size={20}
                        data-cursor-pointer
                        onClick={() => {
                            invertList.splice(index, 1)
                            setInvertList(deepClone(invertList))
                            reduceGood(rec.goods.id)
                        }}
                    />
                </div>
            ),
        },
    ]

    const createReq = useRequest(
        params =>
            $post({
                url: props.inventoryId ? '/warehouse/editinventory' : '/warehouse/createinventory',
                params: params,
            }),
        {
            manual: true,
            onSuccess: () => {
                $msg('success', '添加成功')
                layerManage.pop()
                props.refresh()
            },
        },
    )

    const onSubmit = (submit: boolean = true) => {
        const formatData = invertList.map(list => ({
            goods_id: list.goods.id,
            remark: list.remark,
            stock_actual: list.stock_actual,
        }))

        createReq.run({
            inventory_goods: formatData,
            submit,
            inventory_id: props.inventoryId,
        })
    }

    return (
        <BaseDrawer
            isShow
            title="新建盘点单"
            width={750}
            maskClosable={false}
            onClose={() => {
                layerManage.pop()
            }}
        >
            <Column data-height-100p>
                <Expaned data-overflow-auto>
                    <Spin spinning={loading}>
                        {props.inventoryId && (
                            <StockUpdateDetail
                                status={orderInfo?.status}
                                creator={orderInfo?.creator}
                                update_time={orderInfo?.update_time}
                            />
                        )}
                        <FormTitle title="盘点清单" hasLine={false}>
                            <div
                                data-font-size-16
                                data-margin-right-16
                                className="text-btn-blue"
                                onClick={() => {
                                    showLayer(
                                        <SelectGoods
                                            onSubmit={goods => {
                                                const formatGoods = goods.map(good => ({
                                                    goods: good,
                                                    stock_actual: good.stock,
                                                    remark: '',
                                                }))

                                                const setGoods = uniqBy([...invertList, ...formatGoods], 'goods.id')

                                                setInvertList(setGoods)
                                            }}
                                            filterFn={goods =>
                                                !!goods.goods_package.length || (!goods.on_mall && goods.status === 1)
                                            }
                                        />,
                                    )
                                }}
                            >
                                +添加商品
                            </div>
                        </FormTitle>
                        <div
                            style={{
                                width: 'calc(100% - 32px)',
                                marginLeft: '16px',
                            }}
                        >
                            <Table
                                rowKey={record => record.goods.id}
                                // loading={requestHandle.loading || void 0}
                                bordered
                                pagination={false}
                                columns={columns}
                                dataSource={invertList}
                                scroll={{ x: 'max-content', y: 500 }}
                            />
                        </div>
                    </Spin>
                </Expaned>
                <div className={cls('bottom')}>
                    <Row
                        data-align-items-center
                        className="text-btn-blue"
                        onClick={() => {
                            if (createReq.loading) return
                            onSubmit(false)
                        }}
                    >
                        <i
                            data-margin-right-4
                            data-font-size-20
                            className="iconfont icon6cunweicaogao-20"
                            style={{
                                fontWeight: 500,
                            }}
                        />
                        存为草稿
                    </Row>
                    <Row>
                        <Button
                            disabled={btnDisabled}
                            types="default"
                            loading={createReq.loading || void 0}
                            onClick={() => {
                                onSubmit()
                            }}
                        >
                            确认提交
                        </Button>
                    </Row>
                </div>
            </Column>
        </BaseDrawer>
    )
}

export default StockUpdateForm
